import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  Switch,
  Drawer,
  Typography,
  Pagination,
  Checkbox,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { notificationTemplateAPI, botAPI } from '../services/api';
import InlineButtonEditor, { inlineButtonsRule } from '../components/InlineButtonEditor';
import DynamicVariableEditor, { DynamicVariableConfig } from '../components/DynamicVariableEditor';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface ParamSchema {
  name: string;
  required: boolean;
  default?: string;
  description?: string;
}

interface BoundBot {
  bot_id: number;
  priority: number;
  status: number;
  bot_name: string | null;
  bot_username: string | null;
}

interface NotificationTemplate {
  id: number;
  template_key: string;
  template_name: string;
  content: string;
  parse_mode: string;
  media_type: string | null;
  media_url: string | null;
  inline_buttons: any[][] | null;
  dynamic_variables: DynamicVariableConfig | null;
  params_schema: ParamSchema[] | null;
  bound_bots: BoundBot[];
  status: number;
  created_at: string;
  updated_at: string;
}

interface SendLog {
  id: number;
  bot_id: number;
  template_id: number | null;
  template_key: string | null;
  user_id: number;
  params: Record<string, string> | null;
  rendered_content: string | null;
  message_id: number | null;
  status: 'success' | 'failed';
  error_message: string | null;
  created_at: string;
}

const NotificationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [bots, setBots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [form] = Form.useForm();

  // 发送日志
  const [logsVisible, setLogsVisible] = useState(false);
  const [logs, setLogs] = useState<SendLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTemplateKey, setLogsTemplateKey] = useState<string | undefined>();

  // 参数定义编辑
  const [paramsSchema, setParamsSchema] = useState<ParamSchema[]>([]);

  // 绑定的 bot ID 列表
  const [selectedBotIds, setSelectedBotIds] = useState<number[]>([]);

  const loadBots = useCallback(async () => {
    try {
      const res = await botAPI.getAll();
      setBots(res.data.data || res.data || []);
    } catch (err: any) {
      message.error('加载机器人列表失败');
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationTemplateAPI.getAll();
      setTemplates(res.data.data || []);
    } catch (err: any) {
      message.error('加载模板列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBots(); }, [loadBots]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleCreate = () => {
    setEditingTemplate(null);
    setParamsSchema([]);
    setSelectedBotIds([]);
    form.resetFields();
    form.setFieldsValue({ parse_mode: 'HTML', status: 1, dynamicVariables: null });
    setModalVisible(true);
  };

  const handleEdit = (record: NotificationTemplate) => {
    setEditingTemplate(record);
    setParamsSchema(record.params_schema || []);
    setSelectedBotIds((record.bound_bots || []).map(b => b.bot_id));

    let dynamicVariables: DynamicVariableConfig | null = null;
    if (record.dynamic_variables) {
      try {
        dynamicVariables = typeof record.dynamic_variables === 'string'
          ? JSON.parse(record.dynamic_variables as any)
          : record.dynamic_variables;
      } catch { dynamicVariables = null; }
    }

    form.setFieldsValue({
      template_key: record.template_key,
      template_name: record.template_name,
      content: record.content,
      parse_mode: record.parse_mode,
      media_type: record.media_type || undefined,
      media_url: record.media_url || undefined,
      inline_buttons: record.inline_buttons || [],
      dynamicVariables,
      status: record.status,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedBotIds.length === 0) {
        message.error('请至少选择一个机器人');
        return;
      }

      const data = {
        ...values,
        inline_buttons: values.inline_buttons && values.inline_buttons.length > 0 ? values.inline_buttons : null,
        dynamic_variables: values.dynamicVariables || null,
        params_schema: paramsSchema.length > 0 ? paramsSchema : null,
        media_type: values.media_type || null,
        media_url: values.media_url || null,
        bot_ids: selectedBotIds,
      };
      delete data.dynamicVariables;

      if (editingTemplate) {
        await notificationTemplateAPI.update(editingTemplate.id, data);
        message.success('模板已更新');
      } else {
        await notificationTemplateAPI.create(data);
        message.success('模板已创建');
      }
      setModalVisible(false);
      loadTemplates();
    } catch (err: any) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await notificationTemplateAPI.delete(id);
      message.success('已删除');
      loadTemplates();
    } catch (err: any) {
      message.error('删除失败');
    }
  };

  const handleToggleStatus = async (record: NotificationTemplate) => {
    try {
      await notificationTemplateAPI.update(record.id, { status: record.status === 1 ? 0 : 1 });
      loadTemplates();
    } catch (err: any) {
      message.error('操作失败');
    }
  };

  // 发送日志
  const openLogs = (templateKey?: string) => {
    setLogsTemplateKey(templateKey);
    setLogsPage(1);
    setLogsVisible(true);
    loadLogs(1, templateKey);
  };

  const loadLogs = async (page: number, templateKey?: string) => {
    setLogsLoading(true);
    try {
      const res = await notificationTemplateAPI.getLogs({
        template_key: templateKey,
        page,
        page_size: 20,
      });
      const result = res.data.data;
      setLogs(result.data || []);
      setLogsTotal(result.total || 0);
    } catch (err: any) {
      message.error('加载日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  // 复制调用示例
  const copyCallExample = (record: NotificationTemplate) => {
    const example = {
      user_id: 123456789,
      template_key: record.template_key,
      params: (record.params_schema || []).reduce((acc, p) => {
        acc[p.name] = p.default || `<${p.description || p.name}>`;
        return acc;
      }, {} as Record<string, string>),
    };
    navigator.clipboard.writeText(JSON.stringify(example, null, 2));
    message.success('已复制调用示例到剪贴板');
  };

  // 参数定义编辑器
  const addParam = () => {
    setParamsSchema([...paramsSchema, { name: '', required: true, default: '', description: '' }]);
  };

  const updateParam = (index: number, field: keyof ParamSchema, value: any) => {
    const updated = paramsSchema.map((p, i) => i === index ? { ...p, [field]: value } : p);
    setParamsSchema(updated);
  };

  const removeParam = (index: number) => {
    setParamsSchema(paramsSchema.filter((_, i) => i !== index));
  };

  // 从动态变量配置中提取字段名列表（用于 InlineButtonEditor 的变量提示）
  const getDynamicVarFields = (dvConfig: DynamicVariableConfig | null): string[] => {
    if (!dvConfig) return [];
    const requests = dvConfig.requests || [];
    return requests
      .flatMap((r: any) => r.response_fields || [])
      .filter((f: string) => f.trim());
  };

  const getAuthCodeVarNames = (dvConfig: DynamicVariableConfig | null): string[] => {
    if (!dvConfig) return [];
    return (dvConfig.auth_codes || [])
      .map((c: any) => c.variable_name)
      .filter(Boolean);
  };

  const columns = [
    {
      title: '模板标识',
      dataIndex: 'template_key',
      key: 'template_key',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '名称',
      dataIndex: 'template_name',
      key: 'template_name',
    },
    {
      title: '绑定机器人',
      dataIndex: 'bound_bots',
      key: 'bound_bots',
      render: (boundBots: BoundBot[]) => {
        if (!boundBots || boundBots.length === 0) return <Text type="secondary">未绑定</Text>;
        return boundBots.map(b => (
          <Tag key={b.bot_id} color="cyan">{b.bot_name || `Bot #${b.bot_id}`}</Tag>
        ));
      },
    },
    {
      title: '参数',
      dataIndex: 'params_schema',
      key: 'params_schema',
      render: (schema: ParamSchema[] | null) => {
        if (!schema || schema.length === 0) return <Text type="secondary">无</Text>;
        return schema.map(p => (
          <Tag key={p.name} color={p.required ? 'red' : 'default'}>{p.name}</Tag>
        ));
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, record: NotificationTemplate) => (
        <Switch
          checked={status === 1}
          onChange={() => handleToggleStatus(record)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: NotificationTemplate) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copyCallExample(record)}>调用示例</Button>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => openLogs(record.template_key)}>日志</Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const logColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text: string) => dayjs(text).format('MM-DD HH:mm:ss'),
    },
    { title: '用户 ID', dataIndex: 'user_id', key: 'user_id', width: 120 },
    { title: '模板', dataIndex: 'template_key', key: 'template_key', width: 150 },
    {
      title: 'Bot',
      dataIndex: 'bot_id',
      key: 'bot_id',
      width: 80,
      render: (id: number) => {
        const bot = bots.find(b => b.id === id);
        return bot ? bot.bot_name : `#${id}`;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (s: string) => s === 'success' ? <Tag color="green">成功</Tag> : <Tag color="red">失败</Tag>,
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (text: string | null) => text || '-',
    },
    {
      title: '消息内容',
      dataIndex: 'rendered_content',
      key: 'rendered_content',
      ellipsis: true,
      width: 200,
      render: (text: string | null) => text || '-',
    },
  ];

  return (
    <Card
      title="通知模板管理"
      extra={
        <Space>
          <Button icon={<HistoryOutlined />} onClick={() => openLogs()}>全部日志</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建模板</Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={templates}
        loading={loading}
        pagination={false}
      />

      {/* 编辑 / 创建弹窗 */}
      <Modal
        title={editingTemplate ? '编辑通知模板' : '新建通知模板'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="template_key" label="模板标识 (template_key)" rules={[{ required: true, message: '请输入模板标识' }]}>
            <Input placeholder="如 game_deposit_success（字母/数字/下划线）" disabled={!!editingTemplate} />
          </Form.Item>
          <Form.Item name="template_name" label="模板名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="后台显示用，如：游戏充值成功通知" />
          </Form.Item>

          {/* 绑定机器人（多选） */}
          <Form.Item label="绑定机器人" required extra="选择哪些机器人可以发送此模板的通知，排在前面的优先使用">
            <Checkbox.Group
              value={selectedBotIds}
              onChange={(values) => setSelectedBotIds(values as number[])}
              options={bots.map(bot => ({
                label: bot.bot_name || `Bot #${bot.id}`,
                value: bot.id,
              }))}
            />
          </Form.Item>

          <Alert
            message="可用的内置变量（通知推送场景）"
            description={
              <div style={{ fontSize: 12, lineHeight: '22px' }}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>机器人</div>
                <div style={{ marginBottom: 6 }}>
                  <Text code>{'{bot_id}'}</Text> 机器人 ID &nbsp;
                  <Text code>{'{bot_username}'}</Text> 机器人 @用户名 &nbsp;
                  <Text code>{'{bot_name}'}</Text> 机器人名称 &nbsp;
                  <Text code>{'{bot_invite_link}'}</Text> 机器人链接
                </div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>用户相关</div>
                <div style={{ marginBottom: 6 }}>
                  <Text code>{'{user_id}'}</Text> 用户 Telegram ID &nbsp;
                  <Text code>{'{start}'}</Text> 用户 /start 深度链接参数（如邀请码） &nbsp;
                  <Text type="secondary">+ 调用方通过 params 传入的自定义参数</Text>
                </div>
              </div>
            }
            type="info"
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Form.Item name="content" label="模板内容" rules={[{ required: true, message: '请输入内容' }]}
            extra="使用 {param_name} 作为参数占位符，发送时会被替换（包括外部传入参数、动态变量、登录短码）"
          >
            <TextArea rows={5} placeholder={'充值成功！\n\n订单号：{order_id}\n金额：{amount} {currency}'} />
          </Form.Item>
          <Form.Item name="parse_mode" label="解析模式">
            <Select>
              <Option value="HTML">HTML</Option>
              <Option value="Markdown">Markdown</Option>
              <Option value="MarkdownV2">MarkdownV2</Option>
            </Select>
          </Form.Item>
          <Form.Item name="media_type" label="媒体类型">
            <Select allowClear placeholder="无媒体">
              <Option value="photo">图片</Option>
              <Option value="video">视频</Option>
              <Option value="document">文件</Option>
            </Select>
          </Form.Item>
          <Form.Item name="media_url" label="媒体 URL" extra="支持 {param} 占位符">
            <Input placeholder="https://example.com/image.jpg 或 {image_url}" />
          </Form.Item>

          {/* 动态变量（外部API + 登录短码） */}
          <Form.Item label="动态变量（外部API + 登录短码）" name="dynamicVariables">
            <DynamicVariableEditor />
          </Form.Item>

          {/* Inline 按钮 - 传入动态变量和登录短码的变量名提示 */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const dvConfig = getFieldValue('dynamicVariables');
              const dvFields = getDynamicVarFields(dvConfig);
              const authCodeNames = getAuthCodeVarNames(dvConfig);
              return (
                <Form.Item label="Inline 按钮" name="inline_buttons" rules={[inlineButtonsRule]}>
                  <InlineButtonEditor
                    dynamicVarFields={dvFields}
                    authCodeVarNames={authCodeNames}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* 参数定义 */}
          <Form.Item label="参数定义 (params_schema)" extra="定义调用时可传入的参数，用于校验和默认值填充">
            <Space direction="vertical" style={{ width: '100%' }}>
              {paramsSchema.map((param, idx) => (
                <Space key={idx} align="start" style={{ display: 'flex' }}>
                  <Input
                    placeholder="参数名"
                    value={param.name}
                    onChange={e => updateParam(idx, 'name', e.target.value)}
                    style={{ width: 120 }}
                  />
                  <Select
                    value={param.required ? 'required' : 'optional'}
                    onChange={v => updateParam(idx, 'required', v === 'required')}
                    style={{ width: 90 }}
                  >
                    <Option value="required">必填</Option>
                    <Option value="optional">可选</Option>
                  </Select>
                  <Input
                    placeholder="默认值"
                    value={param.default || ''}
                    onChange={e => updateParam(idx, 'default', e.target.value)}
                    style={{ width: 100 }}
                  />
                  <Input
                    placeholder="描述"
                    value={param.description || ''}
                    onChange={e => updateParam(idx, 'description', e.target.value)}
                    style={{ width: 140 }}
                  />
                  <Button danger size="small" onClick={() => removeParam(idx)}>删除</Button>
                </Space>
              ))}
              <Button type="dashed" onClick={addParam} icon={<PlusOutlined />} size="small">
                添加参数
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 发送日志抽屉 */}
      <Drawer
        title={logsTemplateKey ? `发送日志 - ${logsTemplateKey}` : '全部发送日志'}
        open={logsVisible}
        onClose={() => setLogsVisible(false)}
        width={900}
      >
        <Table
          rowKey="id"
          columns={logColumns}
          dataSource={logs}
          loading={logsLoading}
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={logsPage}
            total={logsTotal}
            pageSize={20}
            onChange={(p) => { setLogsPage(p); loadLogs(p, logsTemplateKey); }}
            showTotal={(total) => `共 ${total} 条`}
          />
        </div>
      </Drawer>
    </Card>
  );
};

export default NotificationTemplates;
